<template>
  <div class="content">
    <div class="spinner" v-if="showSpinner && (time == 0 || hideBar)">
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="8" cy="8" r="7" stroke-width="2" />
      </svg>
    </div>
    <div class="bar-container" v-if="showBar && time > 0">
      <div class="bar" id="bar"></div>
    </div>
    <div class="info" v-if="message.length">
      <p>{{ message }}</p>
    </div>
  </div>
</template>

<script>
  export default {
    props: {
      time: {
        type: Number,
        default: 0,
      },
      initialMessage: {
        type: String,
        default: '',
      },
      slowTime: {
        type: Number,
        default: 0,
      },
      slowMessage: {
        type: String,
        default: 'This is taking longer than usual...',
      },
      failMessage: {
        type: String,
        default: 'Something went wrong.\nPlease try again later.',
      },
      hideBar: {
        type: Boolean,
        default: false,
      },
      hideLateBar: {
        type: Boolean,
        default: false,
      },
    },
    mounted() {
      this.showBar = !this.hideBar;
      if (this.time > 0) this.startInitialLoading();
    },
    methods: {
      wait(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
      },
      animateBar({ element, duration, interval = 250 }) {
        const bar = element;
        return new Promise((resolve) => {
          let progress = 0;
          bar.style.width = '0%';
          const timer = setInterval(() => {
            if (!bar || this.isDestroyed) {
              clearInterval(timer);
              resolve();
            }
            progress += interval;
            const currentWidth = Math.round((progress / duration) * 100);
            bar.style.width = `${Math.min(currentWidth, 100)}%`;
            if (progress >= duration) {
              clearInterval(timer);
              resolve();
            }
          }, interval);
        });
      },
      async startInitialLoading() {
        this.showSpinner = this.hideBar;
        this.message = this.initialMessage;
        await this.$nextTick();
        const bar = document.getElementById('bar');
        if (!bar || this.isDestroyed)
          await new Promise((r) => setTimeout(r, this.time * 1000));
        else {
          await this.animateBar({ element: bar, duration: this.time * 1000 });
        }
        await this.wait(5000);
        if (this.slowTime > 0) await this.startSlowLoading();
        else this.startFailState();
      },
      async startSlowLoading() {
        this.showBar = !this.hideLateBar;
        this.showSpinner = this.hideLateBar;
        this.message = this.slowMessage;
        await this.$nextTick();
        const bar = document.getElementById('bar');
        if (!bar || this.isDestroyed)
          await new Promise((r) => setTimeout(r, this.time * 1000));
        else {
          bar.classList.add('failing');
          await this.animateBar({
            element: bar,
            duration: this.slowTime * 1000,
          });
        }
        await this.wait(5000);
        if (!this.isDestroyed) this.startFailState();
      },
      startFailState() {
        this.message = this.failMessage;
        this.showBar = false;
        this.showSpinner = false;
      },
    },
    data() {
      return {
        showSpinner: true,
        showBar: true,
        message: '',
        loading: false,
        isDestroyed: false,
      };
    },
    beforeUnmount() {
      this.isDestroyed = true;
    },
  };
</script>

<style scoped>
  * {
    user-select: none;
  }

  .spinner {
    width: 42px;
    height: 42px;
  }

  .spinner svg {
    animation: rotation 1455ms infinite linear;
    width: 100%;
    height: 100%;
    overflow: visible;
  }

  .spinner circle {
    stroke: currentColor;
    stroke-dasharray: 44px;
    stroke-dashoffset: 11px;
    transform: rotatey(180deg) rotate(90deg);
    transform-origin: center;
    animation: spinner 3850ms infinite ease;
  }

  @keyframes spinner {
    0% {
      stroke-dashoffset: 33px;
      transform: rotatey(0deg) rotate(0deg);
    }
    24.9999% {
      stroke-dashoffset: 11px;
      transform: rotatey(0deg) rotate(0deg);
    }
    25% {
      stroke-dashoffset: 11px;
      transform: rotatey(180deg) rotate(270deg);
    }
    49.9999% {
      stroke-dashoffset: 33px;
      transform: rotatey(180deg) rotate(270deg);
    }
    50% {
      stroke-dashoffset: 33px;
      transform: rotatey(0deg) rotate(180deg);
    }
    74.9999% {
      stroke-dashoffset: 11px;
      transform: rotatey(0deg) rotate(180deg);
    }
    75% {
      stroke-dashoffset: 11px;
      transform: rotatey(180deg) rotate(90deg);
    }
    100% {
      stroke-dashoffset: 33px;
      transform: rotatey(180deg) rotate(90deg);
    }
  }

  @keyframes rotation {
    100% {
      transform: rotate(360deg);
    }
  }

  .content {
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 20;
    height: 100%;
  }

  .content:has(.bar-container),
  .content:has(.info) {
    flex-direction: column;
    gap: 1rem;
  }

  .bar-container {
    position: relative;
    box-sizing: content-box;
    border: var(--border);
    border-radius: 0.75rem;
    background-color: var(--surface);
    width: 50vw;
    max-width: 100%;
    height: 0.75rem;
  }

  .bar {
    position: absolute;
    top: 0;
    left: 0;
    animation: barPulse 3s linear infinite;
    box-sizing: border-box;
    margin: 0.125rem;
    border-radius: 0.5rem;
    background:
      linear-gradient(
        -45deg,
        transparent 15%,
        rgba(255, 255, 255, 0.2) 25%,
        transparent 35%,
        transparent 65%,
        rgba(255, 255, 255, 0.2) 75%,
        transparent 85%
      ),
      var(--success);
    background-size:
      100vw 100%,
      100% 100%;
    width: 0%;
    max-width: calc(50vw - 0.25rem);
    height: 0.5rem;
  }

  .bar.failing {
    background-color: var(--error);
  }

  @keyframes barPulse {
    0% {
      background-position:
        0 50%,
        0% 0%;
    }
    100% {
      background-position:
        100vw 50%,
        0% 0%;
    }
  }

  .info {
    max-width: 50vw;
  }
</style>
